/**
 * Design tokens — the single source of truth for the app's colour palette.
 *
 * Both consumers read from here so MUI and Tailwind stay in sync:
 *  - `theme.ts` feeds these (hex) values into MUI's `createTheme`. They stay
 *    plain strings, so `alpha(theme.palette.X, n)` and every existing `sx` /
 *    `styled` block keep working unchanged.
 *  - `scripts/gen-tokens-css.ts` walks this object and emits `tokens.css`
 *    (`:root { --app-…: <hex> }`), which Tailwind references via `@theme`.
 *
 * Keep this file import-free and value-only so the generator can run it under
 * plain Node (type-stripping). After editing, run `pnpm run gen:tokens`.
 */

export const primary = {
  light: '#374B9C',
  main: '#0F1324',
  50: '#EFF7FE',
  100: '#E2EFFD',
  200: '#CAE0FB',
  300: '#AACBF7',
  400: '#88ADF1',
  500: '#6C8DE8',
  600: '#506BDB',
  700: '#374B9C',
  800: '#374B9C',
  900: '#33427C',
  950: '#0F1324'
} as const;

export const secondary = {
  main: '#F57A3D',
  50: '#FEF5EE',
  100: '#FEE8D6',
  200: '#FBCEAD',
  300: '#F9AB78',
  400: '#F57A3D',
  500: '#F25B1D',
  600: '#E34113',
  700: '#BD2F11',
  800: '#962716',
  900: '#792315',
  950: '#410E09'
} as const;

export const grey = {
  50: '#F6F6F6',
  100: '#E7E7E7',
  200: '#D1D1D1',
  300: '#B0B0B0',
  400: '#888888',
  500: '#6D6D6D',
  600: '#5D5D5D',
  700: '#4F4F4F',
  800: '#454545',
  900: '#3D3D3D'
} as const;

export const backgroundDark = {
  400: '#4629F2',
  500: '#1D1748',
  600: '#0D082C'
} as const;

export const success = { main: '#007D3E' } as const;
export const error = { main: '#CC071E', light: '#FF6B7A' } as const;
export const info = { main: '#0065CB' } as const;

/**
 * Quality level colours (flow-chart label palette). Keyed by lowercase
 * QualityLevel name; `theme.ts` maps the QualityLevel enum onto these.
 */
export const qualityLevel = {
  unset: '#808080',
  good: '#4CAF50',
  acceptable: '#FFEB3B',
  bad: '#F44336'
} as const;

/**
 * Conversation score tier colours. Excellent ≥75, OnTrack ≥25, NeedsWork <25,
 * Pending = no score yet.
 */
export const tier = {
  excellent: { dot: '#4CAF50', fg: '#2E7D32', bg: '#E8F5E9' },
  onTrack: { dot: '#E8A100', fg: '#8A6D00', bg: '#FFF8D6' },
  needsWork: { dot: '#CC071E', fg: '#8E0014', bg: '#FDECEE' },
  pending: { dot: '#8A8A8A', fg: '#5C5C5C', bg: 'rgba(15, 19, 36, 0.06)' }
} as const;

/** Reusable elevation shadows. Keeps both V2 panel shadows in one place. */
export const elevation = {
  panel: '0 4px 12px -4px rgba(15, 19, 36, 0.05)',
  pill: '0 1px 2px rgba(15, 19, 36, 0.08)',
  // Soft primary-tinted card shadow used by the editorial card surfaces.
  report: '0 1px 0 rgba(15, 19, 36, 0.04), 0 6px 18px -10px rgba(15, 19, 36, 0.10)',
  reportHover: '0 2px 0 rgba(15, 19, 36, 0.06), 0 14px 28px -14px rgba(15, 19, 36, 0.18)'
} as const;

/**
 * Flat record of every token, used by the CSS-var generator. The key becomes
 * the custom-property name: `primary.main` → `--app-primary-main`.
 */
export const tokens = {
  primary,
  secondary,
  grey,
  'background-dark': backgroundDark,
  success,
  error,
  info,
  'quality-level': qualityLevel,
  tier,
  elevation
} as const;
